import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { UserPlus, UserCheck, Clock, Calendar as CalendarIcon, Award, GraduationCap, Briefcase, X, Plus, Wrench, Euro } from 'lucide-react';
import { format, getWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import { SKILLS } from '../utils/skills';

const EmployeeForm = ({ onCreateEmployee, editingEmployee, onUpdateEmployee, isOpen, onOpenChange }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    weeklyHours: '', // Heures hebdomadaires au lieu de mensuelles
    workDays: ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'],
    annualLeave: '',
    specificLeaveDates: [], // Dates précises de congés
    sickLeaveDates: [], // Dates d'arrêt maladie
    trainingWeeks: [], // Semaines de formation pour apprentis
    contractStartDate: '', // Date de debut de contrat (embauche)
    contractEndDate: '', // Date de fin de contrat
    isQualified: false,
    isApprentice: false,
    isSubcontractor: false, // Sous-traitance / intérim : main-d'œuvre externe
    productivityCoefficient: '1', // Coefficient d'heures pondérées (1 = plein rendement)
    productionPresenceCoefficient: '1', // Part du temps passée sur les chantiers (planning seulement)
    hourlyCost: '', // Coût horaire chargé (€/h)
    declaredInHours: false, // Déclaré au comptable en heures de présence (poste adapté), pas en jours
    declaredMonthlyHours: '16',
    declaredInHoursFrom: '', // Avant cette date, déclaré en jours (vide = depuis toujours)
    active: true,
    skills: [] // Spécialités (matchées avec les spécialités requises des chantiers)
  });
  const [selectedLeaveRange, setSelectedLeaveRange] = useState(undefined);
  const [showLeaveCalendar, setShowLeaveCalendar] = useState(false);
  const [selectedTrainingWeek, setSelectedTrainingWeek] = useState(null);
  const [showTrainingCalendar, setShowTrainingCalendar] = useState(false);

  // Charger les données si on édite un employé
  React.useEffect(() => {
    if (editingEmployee) {
      setFormData({
        firstName: editingEmployee.firstName || '',
        lastName: editingEmployee.lastName || '',
        weeklyHours: editingEmployee.weeklyHours?.toString() || '',
        workDays: editingEmployee.workDays || ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'],
        annualLeave: editingEmployee.annualLeave?.toString() || '',
        specificLeaveDates: editingEmployee.specificLeaveDates || [],
        sickLeaveDates: editingEmployee.sickLeaveDates || [],
        trainingWeeks: editingEmployee.trainingWeeks || [],
        contractStartDate: editingEmployee.contractStartDate || '',
        contractEndDate: editingEmployee.contractEndDate || '',
        isQualified: editingEmployee.isQualified || false,
        isApprentice: editingEmployee.isApprentice || false,
        isSubcontractor: editingEmployee.isSubcontractor || false,
        hourlyCost: editingEmployee.hourlyCost !== undefined && editingEmployee.hourlyCost !== null
          ? editingEmployee.hourlyCost.toString()
          : '',
        active: editingEmployee.active !== false,
        declaredInHours: editingEmployee.declaredInHours === true,
        declaredInHoursFrom: editingEmployee.declaredInHoursFrom || '',
        declaredMonthlyHours: editingEmployee.declaredMonthlyHours !== undefined
          && editingEmployee.declaredMonthlyHours !== null
          ? editingEmployee.declaredMonthlyHours.toString()
          : '16',
        productionPresenceCoefficient: editingEmployee.productionPresenceCoefficient !== undefined
          && editingEmployee.productionPresenceCoefficient !== null
          ? editingEmployee.productionPresenceCoefficient.toString()
          : '1',
        productivityCoefficient: editingEmployee.productivityCoefficient !== undefined && editingEmployee.productivityCoefficient !== null
          ? editingEmployee.productivityCoefficient.toString()
          : (editingEmployee.isApprentice ? '0.5' : '1'),
        skills: editingEmployee.skills || []
      });
    } else {
      // Reset form for new employee
      setFormData({
        firstName: '',
        lastName: '',
        weeklyHours: '',
        workDays: ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'],
        annualLeave: '',
        specificLeaveDates: [],
        sickLeaveDates: [],
        trainingWeeks: [],
        contractStartDate: '',
        contractEndDate: '',
        isQualified: false,
        isApprentice: false,
        isSubcontractor: false,
        productivityCoefficient: '1',
        productionPresenceCoefficient: '1',
        hourlyCost: '',
        declaredInHours: false,
        declaredMonthlyHours: '16',
        declaredInHoursFrom: '',
        active: true,
        skills: []
      });
    }
  }, [editingEmployee]);

  const weekDays = [
    { value: 'lundi', label: 'Lundi' },
    { value: 'mardi', label: 'Mardi' },
    { value: 'mercredi', label: 'Mercredi' },
    { value: 'jeudi', label: 'Jeudi' },
    { value: 'vendredi', label: 'Vendredi' },
    { value: 'samedi', label: 'Samedi' },
    { value: 'dimanche', label: 'Dimanche' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.weeklyHours || !formData.annualLeave) return;

    // Calcul des heures vendables annuelles
    // Sans jour travaillé (salarié parti, par ex.), les divisions par le nombre de jours donnaient
    // NaN, envoyé au serveur comme « null » : la fiche devenait illisible et bloquait le chargement
    // de toute l'équipe (vécu le 2026-10-23). Aucun jour travaillé = aucune heure vendable.
    const workingDaysPerWeek = Math.max(1, formData.workDays.length);
    const hoursPerWorkingDay = parseFloat(formData.weeklyHours) / workingDaysPerWeek;
    const weeksPerYear = 52;
    const vacationWeeks = Math.ceil(parseInt(formData.annualLeave) / workingDaysPerWeek);
    const trainingWeeksCount = formData.trainingWeeks.length;
    const workingWeeksPerYear = weeksPerYear - vacationWeeks - trainingWeeksCount;
    // Vendables : ce qu'il peut réellement passer sur les chantiers, congés et
    // formation déduits, puis sa part de présence en production (demande de
    // Samuel) — quelqu'un qui passe la moitié de ses journées aux devis ne vend
    // pas ses heures de bureau.
    const presence = parseFloat(formData.productionPresenceCoefficient) || 1;
    const sellable = Math.round(workingWeeksPerYear * parseFloat(formData.weeklyHours) * presence);
    const annualSellableHours = formData.workDays.length === 0 || !Number.isFinite(sellable) ? 0 : Math.max(0, sellable);

    if (editingEmployee) {
      // Mode édition
      const updatedEmployee = {
        ...editingEmployee,
        firstName: formData.firstName,
        lastName: formData.lastName,
        fullName: `${formData.firstName} ${formData.lastName}`,
        weeklyHours: parseFloat(formData.weeklyHours),
        workDays: formData.workDays,
        annualLeave: parseInt(formData.annualLeave),
        specificLeaveDates: formData.specificLeaveDates,
        sickLeaveDates: formData.sickLeaveDates,
        trainingWeeks: formData.trainingWeeks,
        annualSellableHours: annualSellableHours,
        contractStartDate: formData.contractStartDate,
        contractEndDate: formData.contractEndDate,
        isQualified: formData.isQualified,
        isApprentice: formData.isApprentice,
        isSubcontractor: formData.isSubcontractor,
        productivityCoefficient: parseFloat(formData.productivityCoefficient) || 1,
        productionPresenceCoefficient: parseFloat(formData.productionPresenceCoefficient) || 1,
        hourlyCost: formData.hourlyCost === '' ? null : parseFloat(formData.hourlyCost),
        active: formData.active,
        declaredInHours: formData.declaredInHours,
        declaredMonthlyHours: parseFloat(formData.declaredMonthlyHours) || 16,
        declaredInHoursFrom: formData.declaredInHoursFrom || '',
        skills: formData.skills
      };
      onUpdateEmployee(updatedEmployee);
    } else {
      // Mode création
      const newEmployee = {
        id: Date.now(),
        firstName: formData.firstName,
        lastName: formData.lastName,
        fullName: `${formData.firstName} ${formData.lastName}`,
        weeklyHours: parseFloat(formData.weeklyHours),
        workDays: formData.workDays,
        annualLeave: parseInt(formData.annualLeave),
        specificLeaveDates: formData.specificLeaveDates,
        sickLeaveDates: formData.sickLeaveDates,
        trainingWeeks: formData.trainingWeeks,
        annualSellableHours: annualSellableHours,
        contractStartDate: formData.contractStartDate,
        contractEndDate: formData.contractEndDate,
        isQualified: formData.isQualified,
        isApprentice: formData.isApprentice,
        isSubcontractor: formData.isSubcontractor,
        productivityCoefficient: parseFloat(formData.productivityCoefficient) || 1,
        productionPresenceCoefficient: parseFloat(formData.productionPresenceCoefficient) || 1,
        hourlyCost: formData.hourlyCost === '' ? null : parseFloat(formData.hourlyCost),
        active: formData.active,
        declaredInHours: formData.declaredInHours,
        declaredMonthlyHours: parseFloat(formData.declaredMonthlyHours) || 16,
        declaredInHoursFrom: formData.declaredInHoursFrom || '',
        skills: formData.skills,
        currentAssignments: [],
        status: 'available'
      };
      onCreateEmployee(newEmployee);
    }

    // Reset form
    setFormData({
      firstName: '',
      lastName: '',
      weeklyHours: '',
      workDays: ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'],
      annualLeave: '',
      specificLeaveDates: [],
      sickLeaveDates: [],
      trainingWeeks: [],
      contractStartDate: '',
      contractEndDate: '',
      isQualified: false,
      isApprentice: false,
      isSubcontractor: false,
      productivityCoefficient: '1',
      productionPresenceCoefficient: '1',
      hourlyCost: '',
      declaredInHours: false,
      declaredMonthlyHours: '16',
      declaredInHoursFrom: '',
      active: true,
      skills: []
    });
    setSelectedLeaveRange(undefined);
    setShowLeaveCalendar(false);
    setShowTrainingCalendar(false);
    onOpenChange(false);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleApprenticeToggle = (checked) => {
    setFormData(prev => ({
      ...prev,
      isApprentice: checked,
      // Pré-remplit un coefficient réduit à l'activation, seulement s'il n'a pas déjà été personnalisé
      productivityCoefficient: checked && prev.productivityCoefficient === '1'
        ? '0.5'
        : (!checked && prev.productivityCoefficient === '0.5' ? '1' : prev.productivityCoefficient)
    }));
  };

  const handleSkillToggle = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  const handleWorkDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      workDays: prev.workDays.includes(day)
        ? prev.workDays.filter(d => d !== day)
        : [...prev.workDays, day]
    }));
  };

  const handleAddLeaveRange = () => {
    if (!selectedLeaveRange?.from) return;
    const start = selectedLeaveRange.from;
    const end = selectedLeaveRange.to || selectedLeaveRange.from;

    const daysInRange = [];
    const cursor = new Date(start);
    while (cursor <= end) {
      daysInRange.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }

    setFormData(prev => {
      const merged = [...prev.specificLeaveDates];
      daysInRange.forEach(day => {
        if (!merged.some(d => d.getTime() === day.getTime())) {
          merged.push(day);
        }
      });
      merged.sort((a, b) => a - b);
      return { ...prev, specificLeaveDates: merged };
    });

    setSelectedLeaveRange(undefined);
    setShowLeaveCalendar(false);
  };

  const handleRemoveLeaveDate = (dateToRemove) => {
    setFormData(prev => ({
      ...prev,
      specificLeaveDates: prev.specificLeaveDates.filter(date => date.getTime() !== dateToRemove.getTime())
    }));
  };

  const handleAddTrainingWeek = (startDate) => {
    if (startDate && !formData.trainingWeeks.some(week => week.start.getTime() === startDate.getTime())) {
      // Calculer la fin de semaine
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      
      const newWeek = {
        start: startDate,
        end: endDate,
        weekNumber: getWeek(startDate)
      };
      
      setFormData(prev => ({
        ...prev,
        trainingWeeks: [...prev.trainingWeeks, newWeek].sort((a, b) => a.start - b.start)
      }));
    }
    setShowTrainingCalendar(false);
  };

  const handleRemoveTrainingWeek = (weekToRemove) => {
    setFormData(prev => ({
      ...prev,
      trainingWeeks: prev.trainingWeeks.filter(week => week.start.getTime() !== weekToRemove.start.getTime())
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      {!editingEmployee && (
        <DialogTrigger asChild>
          <Button className="bg-blue-500 hover:bg-blue-600 text-white transition-all duration-200 shadow-lg hover:shadow-xl">
            <UserPlus className="w-4 h-4 mr-2" />
            Nouveau Salarié
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-lg bg-white border border-blue-100 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center text-blue-800">
            <UserPlus className="w-5 h-5 mr-2" />
            {editingEmployee ? 'Modifier le salarié' : 'Créer un nouveau salarié'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-blue-700">Prénom</Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                placeholder="Jean"
                className="border-blue-200 focus:border-blue-400 focus:ring-blue-200"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName" className="text-blue-700">Nom</Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                placeholder="Dupont"
                className="border-blue-200 focus:border-blue-400 focus:ring-blue-200"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="weeklyHours" className="text-blue-700 flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                Heures/semaine
              </Label>
              <Input
                id="weeklyHours"
                type="number"
                step="0.5"
                value={formData.weeklyHours}
                onChange={(e) => handleInputChange('weeklyHours', e.target.value)}
                placeholder="35"
                className="border-blue-200 focus:border-blue-400 focus:ring-blue-200"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="annualLeave" className="text-blue-700 flex items-center">
                <CalendarIcon className="w-4 h-4 mr-1" />
                Congés annuels (jours)
              </Label>
              <Input
                id="annualLeave"
                type="number"
                value={formData.annualLeave}
                onChange={(e) => handleInputChange('annualLeave', e.target.value)}
                placeholder="25"
                className="border-blue-200 focus:border-blue-400 focus:ring-blue-200"
                required
              />
            </div>
          </div>

          {/* Gestion des congés spécifiques */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-blue-700">Congés programmés</Label>
              <Popover
                open={showLeaveCalendar}
                onOpenChange={(open) => {
                  setShowLeaveCalendar(open);
                  if (!open) setSelectedLeaveRange(undefined);
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-blue-200 text-blue-700 hover:bg-blue-50"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Ajouter un congé
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    selected={selectedLeaveRange}
                    onSelect={setSelectedLeaveRange}
                    disabled={(date) => date < new Date()}
                    initialFocus
                    locale={fr}
                  />
                  <div className="flex items-center justify-between p-3 border-t border-blue-100">
                    <span className="text-xs text-blue-600">
                      {selectedLeaveRange?.from
                        ? `${format(selectedLeaveRange.from, 'dd/MM/yyyy', { locale: fr })}${
                            selectedLeaveRange.to
                              ? ` → ${format(selectedLeaveRange.to, 'dd/MM/yyyy', { locale: fr })}`
                              : ''
                          }`
                        : 'Sélectionnez une date (ou une plage)'}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleAddLeaveRange}
                      disabled={!selectedLeaveRange?.from}
                      className="bg-blue-500 hover:bg-blue-600 text-white"
                    >
                      Ajouter
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            
            {formData.specificLeaveDates.length > 0 && (
              <div className="border border-blue-200 rounded-lg p-3 max-h-32 overflow-y-auto">
                <div className="space-y-2">
                  {formData.specificLeaveDates.map((date, index) => (
                    <div key={index} className="flex items-center justify-between bg-blue-50 p-2 rounded">
                      <span className="text-sm text-blue-700">
                        {format(date, 'EEEE dd MMMM yyyy', { locale: fr })}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveLeaveDate(date)}
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {formData.specificLeaveDates.length === 0 && (
              <p className="text-xs text-blue-600 italic">
                Aucun congé programmé. Utilisez le bouton ci-dessus pour ajouter des dates.
              </p>
            )}
          </div>

          {/* Contrat : debut et fin */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contractStartDate" className="text-blue-700 flex items-center">
                <CalendarIcon className="w-4 h-4 mr-1" />
                Date de début de contrat (optionnel)
              </Label>
              <Input
                id="contractStartDate"
                type="date"
                value={formData.contractStartDate}
                onChange={(e) => handleInputChange('contractStartDate', e.target.value)}
                className="border-blue-200 focus:border-blue-400 focus:ring-blue-200"
                max={formData.contractEndDate || undefined}
              />
              <p className="text-xs text-blue-600">
                Date d'embauche : le salarié n'est pas planifié avant. Vide = déjà en poste.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="contractEndDate" className="text-blue-700 flex items-center">
                <CalendarIcon className="w-4 h-4 mr-1" />
                Date de fin de contrat (optionnel)
              </Label>
              <Input
                id="contractEndDate"
                type="date"
                value={formData.contractEndDate}
                onChange={(e) => handleInputChange('contractEndDate', e.target.value)}
                className="border-blue-200 focus:border-blue-400 focus:ring-blue-200"
                min={formData.contractStartDate || undefined}
              />
              <p className="text-xs text-blue-600">
                Laissez vide pour un contrat à durée indéterminée (CDI)
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-blue-700">Jours de présence</Label>
            <div className="grid grid-cols-4 gap-2">
              {weekDays.map(day => (
                <div key={day.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={day.value}
                    checked={formData.workDays.includes(day.value)}
                    onCheckedChange={() => handleWorkDayToggle(day.value)}
                    className="border-blue-300"
                  />
                  <Label htmlFor={day.value} className="text-sm text-blue-700 cursor-pointer">
                    {day.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-blue-700 flex items-center">
              <Wrench className="w-4 h-4 mr-1" />
              Spécialités
            </Label>
            <div className="flex flex-wrap gap-3">
              {SKILLS.map(skill => (
                <label key={skill} htmlFor={`emp-skill-${skill}`} className="flex items-center space-x-1.5 cursor-pointer">
                  <Checkbox
                    id={`emp-skill-${skill}`}
                    checked={formData.skills.includes(skill)}
                    onCheckedChange={() => handleSkillToggle(skill)}
                    className="border-blue-300"
                  />
                  <span className="text-sm text-blue-800">{skill}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-blue-600">
              Utilisées par le moteur : un chantier exigeant une spécialité recevra au moins un salarié qui l'a.
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex items-start space-x-2">
              <Checkbox
                id="active"
                checked={formData.active}
                onCheckedChange={(checked) => handleInputChange('active', checked)}
                className="border-green-300"
              />
              <div className="space-y-1">
                <Label htmlFor="active" className="text-blue-700 cursor-pointer flex items-center">
                  <UserCheck className="w-4 h-4 mr-1" />
                  Salarié actif
                </Label>
                <p className="text-xs text-green-600">
                  Décoché : hors du planning et de la saisie d'heures, fiche conservée
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <Checkbox
                id="qualified"
                checked={formData.isQualified}
                onCheckedChange={(checked) => handleInputChange('isQualified', checked)}
                className="border-blue-300"
              />
              <div className="space-y-1">
                <Label htmlFor="qualified" className="text-blue-700 cursor-pointer flex items-center">
                  <Award className="w-4 h-4 mr-1" />
                  Ouvrier qualifié
                </Label>
                <p className="text-xs text-blue-600">
                  Peut superviser les chantiers complexes
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <Checkbox
                id="apprentice"
                checked={formData.isApprentice}
                onCheckedChange={handleApprenticeToggle}
                className="border-yellow-300"
              />
              <div className="space-y-1">
                <Label htmlFor="apprentice" className="text-blue-700 cursor-pointer flex items-center">
                  <GraduationCap className="w-4 h-4 mr-1" />
                  Apprenti
                </Label>
                <p className="text-xs text-yellow-600">
                  Ne peut pas être seul sur un chantier
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <Checkbox
                id="subcontractor"
                checked={formData.isSubcontractor}
                onCheckedChange={(checked) => handleInputChange('isSubcontractor', checked)}
                className="border-purple-300"
              />
              <div className="space-y-1">
                <Label htmlFor="subcontractor" className="text-blue-700 cursor-pointer flex items-center">
                  <Briefcase className="w-4 h-4 mr-1" />
                  Sous-traitance / intérim
                </Label>
                <p className="text-xs text-purple-600">
                  Main-d'œuvre externe, pas un salarié permanent
                </p>
              </div>
            </div>

            <div className="space-y-2 pl-6">
              <Label htmlFor="productivityCoefficient" className="text-blue-700 flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                Coefficient d'heures pondérées
              </Label>
              <Input
                id="productivityCoefficient"
                type="number"
                step="0.05"
                min="0.1"
                max="1"
                value={formData.productivityCoefficient}
                onChange={(e) => handleInputChange('productivityCoefficient', e.target.value)}
                className="border-blue-200 focus:border-blue-400 focus:ring-blue-200 max-w-[120px]"
              />
              <p className="text-xs text-blue-600">
                1 = plein rendement (ouvrier qualifié). Réduisez pour un apprenti (ex: 0.5 = ses heures comptent pour moitié dans le calcul de durée des chantiers).
              </p>
            </div>

            <div className="space-y-2 pl-6">
              <Label htmlFor="productionPresenceCoefficient" className="text-blue-700 flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                Coefficient de présence en production
              </Label>
              <Input
                id="productionPresenceCoefficient"
                type="number"
                step="0.05"
                min="0"
                max="1"
                value={formData.productionPresenceCoefficient}
                onChange={(e) => handleInputChange('productionPresenceCoefficient', e.target.value)}
                className="border-blue-200 focus:border-blue-400 focus:ring-blue-200 max-w-[120px]"
              />
              <p className="text-xs text-blue-600">
                Part de son temps réellement passée sur les chantiers. 1 = tout son temps ;
                0.5 pour quelqu'un qui partage ses journées avec les devis, la gestion ou le dépôt.
                <strong> Ne sert qu'au planning</strong> — durée des chantiers, capacité, carnet de charge.
                Les marges et les coûts reposent sur les heures réellement pointées.
              </p>
            </div>

            <div className="space-y-2 pl-6">
              <Label htmlFor="hourlyCost" className="text-blue-700 flex items-center">
                <Euro className="w-4 h-4 mr-1" />
                Coût horaire chargé (€/h)
              </Label>
              <Input
                id="hourlyCost"
                type="number"
                step="0.01"
                min="0"
                value={formData.hourlyCost}
                onChange={(e) => handleInputChange('hourlyCost', e.target.value)}
                className="border-blue-200 focus:border-blue-400 focus:ring-blue-200 max-w-[120px]"
              />
              <p className="text-xs text-blue-600">
                Sert au coût de main-d'œuvre réel des chantiers.
              </p>
            </div>

            <div className="flex items-start space-x-2">
              <Checkbox
                id="declaredInHours"
                checked={formData.declaredInHours}
                onCheckedChange={(checked) => handleInputChange('declaredInHours', checked === true)}
                className="border-blue-300"
              />
              <div className="space-y-1">
                <Label htmlFor="declaredInHours" className="text-blue-700 cursor-pointer flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  Déclaré en heures de présence
                </Label>
                <p className="text-xs text-blue-600">
                  Poste adapté, sans jour fixe : la déclaration au comptable indique ses heures du mois,
                  sans jours ni zones de trajet ni paniers.
                </p>
              </div>
            </div>

            {formData.declaredInHours && (
              <div className="space-y-2 pl-6">
                <Label htmlFor="declaredMonthlyHours" className="text-blue-700 flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  Heures de présence par mois
                </Label>
                <Input
                  id="declaredMonthlyHours"
                  type="number"
                  step="0.5"
                  min="0"
                  value={formData.declaredMonthlyHours}
                  onChange={(e) => handleInputChange('declaredMonthlyHours', e.target.value)}
                  className="border-blue-200 focus:border-blue-400 focus:ring-blue-200 max-w-[120px]"
                />
                <p className="text-xs text-blue-600">
                  16 par défaut. Modifiable mois par mois dans la fenêtre « Déclarer les heures ».
                </p>
                <Label htmlFor="declaredInHoursFrom" className="text-blue-700 flex items-center pt-1">
                  <CalendarIcon className="w-4 h-4 mr-1" />
                  À partir du
                </Label>
                <Input
                  id="declaredInHoursFrom"
                  type="date"
                  value={formData.declaredInHoursFrom}
                  onChange={(e) => handleInputChange('declaredInHoursFrom', e.target.value)}
                  className="border-blue-200 focus:border-blue-400 focus:ring-blue-200 max-w-[180px]"
                />
                <p className="text-xs text-blue-600">
                  Facultatif. Les mois précédant cette date restent déclarés en jours.
                </p>
              </div>
            )}
          </div>

          {/* Gestion des semaines de formation pour apprentis */}
          {formData.isApprentice && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-yellow-700">Semaines de formation</Label>
                <Popover open={showTrainingCalendar} onOpenChange={setShowTrainingCalendar}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-yellow-200 text-yellow-700 hover:bg-yellow-50"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Ajouter une semaine
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedTrainingWeek}
                      onSelect={handleAddTrainingWeek}
                      disabled={(date) => date < new Date()}
                      initialFocus
                      locale={fr}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              {formData.trainingWeeks.length > 0 && (
                <div className="border border-yellow-200 rounded-lg p-3 max-h-32 overflow-y-auto">
                  <div className="space-y-2">
                    {formData.trainingWeeks.map((week, index) => (
                      <div key={index} className="flex items-center justify-between bg-yellow-50 p-2 rounded">
                        <span className="text-sm text-yellow-700">
                          Semaine {week.weekNumber} : {format(week.start, 'dd/MM', { locale: fr })} - {format(week.end, 'dd/MM/yyyy', { locale: fr })}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveTrainingWeek(week)}
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {formData.trainingWeeks.length === 0 && (
                <p className="text-xs text-yellow-600 italic">
                  Aucune semaine de formation programmée.
                </p>
              )}
            </div>
          )}

          {formData.weeklyHours && formData.workDays.length > 0 && (
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <div className="space-y-1 text-sm">
                <p className="text-blue-700">
                  <strong>Heures par jour :</strong> {(parseFloat(formData.weeklyHours) / formData.workDays.length).toFixed(1)}h
                </p>
                {formData.annualLeave && (
                  <>
                    <p className="text-blue-700">
                      <strong>Semaines de congés :</strong> {Math.ceil(parseInt(formData.annualLeave) / formData.workDays.length)} sem.
                    </p>
                    {formData.isApprentice && formData.trainingWeeks.length > 0 && (
                      <p className="text-yellow-700">
                        <strong>Semaines de formation :</strong> {formData.trainingWeeks.length} sem.
                      </p>
                    )}
                    <p className="text-green-700 font-medium">
                      <strong>Heures vendables/an :</strong> {
                        Math.round((52 - Math.ceil(parseInt(formData.annualLeave) / formData.workDays.length) - formData.trainingWeeks.length) * parseFloat(formData.weeklyHours))
                      }h
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {formData.monthlyHours && formData.workDays.length > 0 && (
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700">
                <strong>Capacité hebdomadaire :</strong> {Math.round(parseInt(formData.monthlyHours) / 4.33)} heures
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Répartie sur {formData.workDays.length} jour(s) par semaine
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-blue-200 text-blue-700 hover:bg-blue-50"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              className="bg-blue-500 hover:bg-blue-600 text-white transition-all duration-200"
            >
              {editingEmployee ? 'Mettre à jour' : 'Créer le salarié'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeForm;